import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { LocalDate } from '../../../core/model/local-date';
import { Money } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Company } from '../../../rent/model/company';
import { CompanyBankAccount } from '../../../rent/model/company-bank-account';
import {
  AmortizationPlan,
  AmortizationPlanDocument,
  AmortizationPlanInterestInvoiceAddress,
  AmortizationPlanInterestInvoiceDelivery,
  AmortizationPlanInstallment,
  AmortizationPlanInvoice,
  AmortizationPlanPaymentNotice,
  AmortizationPlanPaymentNoticeDelivery,
} from '../../model/amortization-plan';
import { AmortizationPlanStatus } from '../../model/const/enum';
import {
  AmortizationPlanCreateDefaults,
  AmortizationPlanCreatePreview,
  AmortizationPlanCreatePreviewInvoice,
  AmortizationPlanCreatePreviewRequest,
  AmortizationPlanCreateRequest,
  AmortizationPlanLeaseAgreementOption,
  AmortizationPlanRentPeriodOption,
} from '../../model/amortization-plan-create';
import {
  AmortizationPlanAutomaticPaymentNoticeRequest,
  AmortizationPlanPaymentNoticeExcludedItem,
  AmortizationPlanPaymentNoticePreview,
  AmortizationPlanPaymentNoticePreviewInstallment,
  AmortizationPlanPaymentNoticeRequest,
} from '../../model/amortization-plan-payment-notice';

export interface AmortizationPlanPaymentNoticePaymentModel {
  paymentNoticeId: string;
  amount: Money;
  date: LocalDate;
  defaultCompanyBankAccountId: string;
  companyBankAccounts: CompanyBankAccount[];
}

export interface AmortizationPlanPaymentNoticePaymentRequest {
  paymentNoticeId: string;
  amount: Money;
  date: LocalDate;
  companyBankAccountId: string;
}

export interface AmortizationPlanCancelRequest {
  id: string;
  sendInvoicesToDebtCollection: boolean;
  reason: string;
}

@Injectable()
export class AmortizationPlanService extends DataService<AmortizationPlan> {
  private readonly _defaultFilterFields: string[] = ['status'];

  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, AmortizationPlan, progressBar, 'amortization-plan');
  }

  protected createModel(data: any): AmortizationPlan {
    const model = super.createModel(data);
    model.number = data?.number ?? 0;
    model.companyId = data?.companyId ?? null;
    model.companyName = data?.companyName ?? null;
    model.leaseAgreementId = data?.leaseAgreementId ?? null;
    model.leaseAgreementNumber = data?.leaseAgreementNumber ?? null;
    model.leaseAgreementDisplayName = data?.leaseAgreementDisplayName ?? null;
    model.separateInvoicing = !!data?.separateInvoicing;
    model.createdAt = data?.createdAt ? new Date(data.createdAt) : null;
    model.calculatedInterestAmount = this.mapMoney(data?.calculatedInterestAmount);
    model.interestInvoiceId = data?.interestInvoiceId ?? null;
    model.interestInvoiceNumber = data?.interestInvoiceNumber ?? null;
    model.interestInvoiceAmount = data?.interestInvoiceAmount ? this.mapMoney(data.interestInvoiceAmount) : null;
    model.interestInvoiceDueDate = LocalDate.fromDate(data?.interestInvoiceDueDate);
    model.interestInvoiceDelivery = this.mapInterestInvoiceDelivery(data?.interestInvoiceDelivery);
    model.paymentNoticeDeliveryMethod = typeof data?.paymentNoticeDeliveryMethod === 'number' ? data.paymentNoticeDeliveryMethod : null;
    model.canApprove = !!data?.canApprove;
    model.canWithdrawApproval = !!data?.canWithdrawApproval;
    model.canCancel = !!data?.canCancel;
    model.canSendInvoicesToDebtCollection = !!data?.canSendInvoicesToDebtCollection;
    model.eSignatureErrandId = data?.eSignatureErrandId ?? null;
    model.isESignatureCompleted = !!data?.isESignatureCompleted;
    model.invoices = Array.isArray(data?.invoices) ? data.invoices.map(item => this.mapInvoice(item)) : [];
    model.paymentNotices = Array.isArray(data?.paymentNotices) ? data.paymentNotices.map(item => this.mapPaymentNotice(item)) : [];
    model.installments = Array.isArray(data?.installments) ? data.installments.map(item => this.mapInstallment(item)) : [];
    model.documents = Array.isArray(data?.documents) ? data.documents.map(item => this.mapDocument(item)) : [];
    return model;
  }

  getCreatePreview(model: AmortizationPlanCreatePreviewRequest): Observable<AmortizationPlanCreatePreview> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/preview`, JSON.stringify(model)).pipe(
      map(response => this.mapCreatePreview(response)),
      finalize(() => this.progressBar.stop())
    );
  }

  getCreateDefaults(): Observable<AmortizationPlanCreateDefaults> {
    this.progressBar.start();
    return this.client.get<any>(`${this.endpoint}/default`).pipe(
      map(response => this.mapCreateDefaults(response)),
      finalize(() => this.progressBar.stop())
    );
  }

  getCreateInvoiceIdsForContact(contactId: string): Observable<string[]> {
    this.progressBar.start();
    return this.client.get<string[]>(`${this.endpoint}/contact/${contactId}/invoice-ids`).pipe(finalize(() => this.progressBar.stop()));
  }

  createFromInvoices(model: AmortizationPlanCreateRequest): Observable<AmortizationPlan> {
    this.progressBar.start();
    return this.client.post<any>(this.endpoint, JSON.stringify(model)).pipe(
      map(response => this.createModel(response)),
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.CREATE.SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  previewPaymentNotices(model: AmortizationPlanPaymentNoticeRequest): Observable<AmortizationPlanPaymentNoticePreview> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/payment-notice/preview`, JSON.stringify(model)).pipe(
      map(response => this.mapPaymentNoticePreview(response)),
      finalize(() => this.progressBar.stop())
    );
  }

  createPaymentNotices(model: AmortizationPlanPaymentNoticeRequest): Observable<AmortizationPlanPaymentNoticePreview> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/payment-notice`, JSON.stringify(model)).pipe(
      map(response => this.mapPaymentNoticePreview(response)),
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.PAYMENT_NOTICE.SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  previewAutomaticPaymentNotices(model: AmortizationPlanAutomaticPaymentNoticeRequest): Observable<AmortizationPlanPaymentNoticePreview> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/payment-notice/automatic/preview`, JSON.stringify(model)).pipe(
      map(response => this.mapPaymentNoticePreview(response)),
      finalize(() => this.progressBar.stop())
    );
  }

  createAutomaticPaymentNotices(model: AmortizationPlanAutomaticPaymentNoticeRequest): Observable<AmortizationPlanPaymentNoticePreview> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/payment-notice/automatic`, JSON.stringify(model)).pipe(
      map(response => this.mapPaymentNoticePreview(response)),
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.PAYMENT_NOTICE.SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  requestPaymentNoticeDelivery(paymentNoticeId: string): Observable<AmortizationPlanPaymentNoticeDelivery> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/payment-notice/delivery`, JSON.stringify({ paymentNoticeId })).pipe(
      map(response => this.mapPaymentNoticeDelivery(response)),
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_REQUEST_SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  getPaymentNoticePaymentModel(paymentNoticeId: string): Observable<AmortizationPlanPaymentNoticePaymentModel> {
    this.progressBar.start();
    return this.client.get<any>(`${this.endpoint}/payment-notice/payment/${paymentNoticeId}`).pipe(
      map(response => this.mapPaymentNoticePaymentModel(response)),
      finalize(() => this.progressBar.stop())
    );
  }

  registerPaymentNoticePayment(model: AmortizationPlanPaymentNoticePaymentRequest): Observable<void> {
    this.progressBar.start();
    return this.client.post<void>(`${this.endpoint}/payment-notice/payment`, JSON.stringify(model)).pipe(
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.PAYMENT_NOTICE.PAYMENT_REGISTERED_SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  approve(id: string): Observable<AmortizationPlan> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/${id}/approve`, {}).pipe(
      map(response => this.createModel(response)),
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.APPROVE.SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  withdrawApproval(id: string): Observable<AmortizationPlan> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/${id}/withdraw-approval`, {}).pipe(
      map(response => this.createModel(response)),
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.WITHDRAW_APPROVAL.SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  cancelPlan(model: AmortizationPlanCancelRequest): Observable<AmortizationPlan> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/cancel`, JSON.stringify(model)).pipe(
      map(response => this.createModel(response)),
      tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.CANCEL.SUCCESS')),
      finalize(() => this.progressBar.stop())
    );
  }

  getDataForESign(id: string): Observable<any> {
    this.progressBar.start();
    return this.client.get<any>(`${this.endpoint}/esign-data/${id}`).pipe(
      finalize(() => this.progressBar.stop())
    );
  }

  getPdf(id: string): Observable<Blob> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/pdf/${id}`, { responseType: 'blob' }).pipe(
      finalize(() => this.progressBar.stop())
    );
  }

  getPdfUrl(id: string): string {
    return `${this.endpoint}/pdf/${id}`;
  }

  getPaymentNoticePdf(paymentNoticeId: string): Observable<Blob> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/payment-notice/pdf/${paymentNoticeId}`, { responseType: 'blob' }).pipe(
      finalize(() => this.progressBar.stop())
    );
  }

  sendForESign(context: any): Observable<any> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(context));
    for (const file of context.attachments ?? []) {
      formData.append('attachments[]', file, file.name);
    }

    return this.client.post(`${this.endpoint}/send-for-esign`, formData).pipe(
      catchError(error => {
        this.notification.showError(error);
        return throwError(() => error);
      }),
      tap(() => this.notification.showSuccess('SUCCESS.ESIGN_SENT'))
    );
  }

  mapFilterValue(key: string, value: any): any {
    const mappedValue = super.mapFilterValue(key, value);
    switch (key) {
      case 'status':
      case 'articleId':
      case 'companyId':
      case 'debtorContactId':
        return mappedValue === '' ? null : mappedValue;
      default:
        return mappedValue;
    }
  }

  getFilterDefinition(): Observable<{
    pageSize: number;
    filter: {
      debtorContactId: string;
      articleId: string;
      companyId: string;
      status: AmortizationPlanStatus;
      createdFrom: LocalDate;
      createdTo: LocalDate;
      selectedFields: string[];
      defaultFields: string[];
    };
    companies: Company[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(response => {
        const filter = response?.filter ?? {};
        return {
          pageSize: response?.pageSize,
          filter: {
            debtorContactId: filter.debtorContactId ?? '',
            articleId: filter.articleId ?? '',
            companyId: filter.companyId ?? '',
            status: typeof filter.status === 'number' ? filter.status : null,
            createdFrom: LocalDate.fromDate(filter.createdFrom),
            createdTo: LocalDate.fromDate(filter.createdTo),
            selectedFields: Array.isArray(filter.selectedFields) ? filter.selectedFields : this._defaultFilterFields,
            defaultFields: this._defaultFilterFields,
          },
          companies: Array.isArray(response?.companies) ? response.companies.map(x => Object.assign(new Company(), x)) : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  private mapCreatePreview(response: any): AmortizationPlanCreatePreview {
    return {
      companyId: response?.companyId ?? null,
      companyName: response?.companyName ?? null,
      defaultArticleId: response?.defaultArticleId ?? null,
      debtorContactId: response?.debtorContactId ?? null,
      debtorContactName: response?.debtorContactName ?? null,
      interestInvoiceDelivery: this.mapInterestInvoiceDelivery(response?.interestInvoiceDelivery),
      totalAmount: this.mapMoney(response?.totalAmount),
      canCreate: !!response?.canCreate,
      eligibleInvoices: Array.isArray(response?.eligibleInvoices) ? response.eligibleInvoices.map(item => this.mapPreviewInvoice(item)) : [],
      excludedInvoices: Array.isArray(response?.excludedInvoices) ? response.excludedInvoices.map(item => this.mapPreviewInvoice(item)) : [],
    };
  }

  private mapCreateDefaults(response: any): AmortizationPlanCreateDefaults {
    return {
      status: typeof response?.status === 'number' ? response.status : AmortizationPlanStatus.Draft,
      separateInvoicing: !!response?.separateInvoicing,
      leaseAgreements: Array.isArray(response?.leaseAgreements) ? response.leaseAgreements.map(item => this.mapLeaseAgreementOption(item)) : [],
    };
  }

  private mapLeaseAgreementOption(response: any): AmortizationPlanLeaseAgreementOption {
    return {
      id: response?.id ?? null,
      companyId: response?.companyId ?? null,
      companyName: response?.companyName ?? null,
      leaseAgreementNumber: response?.leaseAgreementNumber ?? null,
      status: typeof response?.status === 'number' ? response.status : null,
      category: typeof response?.category === 'number' ? response.category : null,
      categoryCaption: response?.categoryCaption ?? null,
      rentalObjectCaption: response?.rentalObjectCaption ?? null,
      payerName: response?.payerName ?? null,
      payerContactId: response?.payerContactId ?? null,
      tenantNames: response?.tenantNames ?? null,
      displayName: response?.displayName ?? null,
      firstRentPeriods: Array.isArray(response?.firstRentPeriods) ? response.firstRentPeriods.map(item => this.mapRentPeriodOption(item)) : [],
    };
  }

  private mapRentPeriodOption(response: any): AmortizationPlanRentPeriodOption {
    return {
      id: response?.id ?? null,
      dueDate: LocalDate.fromDate(response?.dueDate),
      fromDate: LocalDate.fromDate(response?.fromDate),
      toDate: LocalDate.fromDate(response?.toDate),
      periodText: response?.periodText ?? null,
      invoicePeriodText: response?.invoicePeriodText ?? null,
      invoicePeriodMonths: response?.invoicePeriodMonths ?? 0,
      displayName: response?.displayName ?? null,
    };
  }

  private mapPreviewInvoice(response: any): AmortizationPlanCreatePreviewInvoice {
    return {
      invoiceId: response?.invoiceId ?? null,
      invoiceNumber: response?.invoiceNumber ?? null,
      invoiceRecipientContactId: response?.invoiceRecipientContactId ?? null,
      invoiceRecipientName: response?.invoiceRecipientName ?? null,
      dueDate: LocalDate.fromDate(response?.dueDate),
      unpaidAmount: this.mapMoney(response?.unpaidAmount),
      isIncluded: !!response?.isIncluded,
      selected: !!response?.isIncluded,
      exclusionReason: response?.exclusionReason ?? null,
      hasWarning: !!response?.hasWarning,
      warningMessage: response?.warningMessage ?? null,
    };
  }

  private mapInterestInvoiceDelivery(response: any): AmortizationPlanInterestInvoiceDelivery {
    return {
      deliveryMethod: typeof response?.deliveryMethod === 'number' ? response.deliveryMethod : null,
      email: response?.email ?? null,
      eInvoiceAddress: response?.eInvoiceAddress ?? null,
      address: this.mapInterestInvoiceAddress(response?.address),
    };
  }

  private mapInterestInvoiceAddress(response: any): AmortizationPlanInterestInvoiceAddress {
    return {
      name: response?.name ?? null,
      additionalName: response?.additionalName ?? null,
      street: response?.street ?? null,
      postalCode: response?.postalCode ?? null,
      city: response?.city ?? null,
      country: response?.countryCode ?? null,
    };
  }

  private mapInstallment(response: any): AmortizationPlanInstallment {
    return {
      id: response?.id ?? null,
      invoiceRowId: response?.invoiceRowId ?? null,
      paymentNoticeRowId: response?.paymentNoticeRowId ?? null,
      dueDate: LocalDate.fromDate(response?.dueDate),
      amount: this.mapMoney(response?.amount),
      notice: response?.notice ?? null,
      paidAmount: this.mapMoney(response?.paidAmount),
      creditedAmount: this.mapMoney(response?.creditedAmount),
      customerLossAmount: this.mapMoney(response?.customerLossAmount),
    };
  }

  private mapInvoice(response: any): AmortizationPlanInvoice {
    return {
      invoiceId: response?.invoiceId ?? null,
      invoiceNumber: response?.invoiceNumber ?? null,
      dueDate: LocalDate.fromDate(response?.dueDate),
      remainingAmount: this.mapMoney(response?.remainingAmount),
    };
  }

  private mapPaymentNotice(response: any): AmortizationPlanPaymentNotice {
    return {
      id: response?.id ?? null,
      ocrNumber: response?.ocrNumber ?? null,
      dueDate: LocalDate.fromDate(response?.dueDate),
      amount: this.mapMoney(response?.amount),
      remainingAmount: this.mapMoney(response?.remainingAmount ?? response?.amount),
      createdDate: response?.createdDate ? new Date(response.createdDate) : null,
      sentDate: response?.sentDate ? new Date(response.sentDate) : null,
      sequenceNumber: typeof response?.sequenceNumber === 'number' ? response.sequenceNumber : null,
      deliveryMethod: typeof response?.deliveryMethod === 'number' ? response.deliveryMethod : null,
      deliveryAddress: response?.deliveryAddress ?? null,
      latestDelivery: response?.latestDelivery ? this.mapPaymentNoticeDelivery(response.latestDelivery) : null,
      canRequestDelivery: response?.canRequestDelivery === true,
      deliveries: Array.isArray(response?.deliveries) ? response.deliveries.map(item => this.mapPaymentNoticeDelivery(item)) : [],
    };
  }

  private mapPaymentNoticeDelivery(response: any): AmortizationPlanPaymentNoticeDelivery {
    return {
      id: response?.id ?? null,
      method: typeof response?.method === 'number' ? response.method : null,
      status: typeof response?.status === 'number' ? response.status : null,
      createdDate: response?.createdDate ? new Date(response.createdDate) : null,
      lastAttemptedAt: response?.lastAttemptedAt ? new Date(response.lastAttemptedAt) : null,
      sentDate: response?.sentDate ? new Date(response.sentDate) : null,
      attemptCount: typeof response?.attemptCount === 'number' ? response.attemptCount : 0,
      deliveryAddress: response?.deliveryAddress ?? null,
      errorMessage: response?.errorMessage ?? null,
      provider: response?.provider ?? null,
      providerReference: response?.providerReference ?? null,
      emailDeliveryStatusText: response?.emailDeliveryStatusText ?? null,
    };
  }

  private mapDocument(response: any): AmortizationPlanDocument {
    return Object.assign(new AmortizationPlanDocument(), {
      id: response?.id ?? null,
      documentId: response?.documentId ?? null,
      caption: response?.caption ?? response?.fileName ?? null,
      document: null,
      size: response?.size ?? response?.fileSizeInBytes ?? 0,
      uploadedDate: response?.uploadedDate ? new Date(response.uploadedDate) : null,
      fileName: response?.fileName ?? null,
      fileSizeInBytes: response?.fileSizeInBytes ?? 0,
      sortOrder: response?.sortOrder ?? 0,
      amortizationPlan: null,
    });
  }

  private mapMoney(response: any): Money {
    return new Money(response?.currencyCode ?? response?.currency?.isoCode ?? '', response?.amount ?? 0);
  }

  private mapPaymentNoticePreview(response: any): AmortizationPlanPaymentNoticePreview {
    return {
      canCreate: !!response?.canCreate,
      manualNoticeInstallments: Array.isArray(response?.manualNoticeInstallments)
        ? response.manualNoticeInstallments.map(item => this.mapPaymentNoticePreviewInstallment(item))
        : [],
      rentInvoiceInstallments: Array.isArray(response?.rentInvoiceInstallments)
        ? response.rentInvoiceInstallments.map(item => this.mapPaymentNoticePreviewInstallment(item))
        : [],
      excludedItems: Array.isArray(response?.excludedItems)
        ? response.excludedItems.map(item => this.mapPaymentNoticeExcludedItem(item))
        : [],
    };
  }

  private mapPaymentNoticePaymentModel(response: any): AmortizationPlanPaymentNoticePaymentModel {
    return {
      paymentNoticeId: response?.paymentNoticeId ?? null,
      amount: this.mapMoney(response?.amount),
      date: LocalDate.fromDate(response?.date),
      defaultCompanyBankAccountId: response?.defaultCompanyBankAccountId ?? null,
      companyBankAccounts: Array.isArray(response?.companyBankAccounts)
        ? response.companyBankAccounts.map(item => Object.assign(new CompanyBankAccount(), item))
        : [],
    };
  }

  private mapPaymentNoticePreviewInstallment(response: any): AmortizationPlanPaymentNoticePreviewInstallment {
    return {
      installmentId: response?.installmentId ?? null,
      amortizationPlanId: response?.amortizationPlanId ?? null,
      companyName: response?.companyName ?? null,
      debtorContactName: response?.debtorContactName ?? null,
      dueDate: LocalDate.fromDate(response?.dueDate),
      amount: this.mapMoney(response?.amount),
      description: response?.description ?? null,
      matchingLeaseAgreementNumber: response?.matchingLeaseAgreementNumber ?? null,
      matchingRentPeriodText: response?.matchingRentPeriodText ?? null,
      manualOverrideSelected: !!response?.manualOverrideSelected,
      canForceManual: response?.canForceManual !== false,
    };
  }

  private mapPaymentNoticeExcludedItem(response: any): AmortizationPlanPaymentNoticeExcludedItem {
    return {
      amortizationPlanId: response?.amortizationPlanId ?? null,
      installmentId: response?.installmentId ?? null,
      companyName: response?.companyName ?? null,
      debtorContactName: response?.debtorContactName ?? null,
      dueDate: LocalDate.fromDate(response?.dueDate),
      amount: response?.amount ? this.mapMoney(response?.amount) : null,
      reason: response?.reason ?? null,
    };
  }
}
